export const SITE_URL = 'https://noorddigital.nl'

const postalAddress = {
  '@type': 'PostalAddress',
  streetAddress: 'Schuit 8a',
  postalCode: '1724 BD',
  addressLocality: 'Oudkarspel',
  addressRegion: 'Noord-Holland',
  addressCountry: 'NL',
}

const providerReference = {
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#organisatie`,
  name: 'Noord Digital',
  url: SITE_URL,
  telephone: '+31653689686',
  address: postalAddress,
}

const serviceCatalog = [
  {
    name: 'Websites',
    path: '/websites',
    description:
      'Websites voor dienstverlenende bedrijven, gebouwd om gevonden te worden en vertrouwen te wekken.',
  },
  {
    name: 'Digitale vormgeving',
    path: '/vormgeving',
    description:
      'Logo, huisstijl, drukwerk en digitale sjablonen, opgeleverd als aanpasbaar Figma-document.',
  },
  {
    name: 'Hosting',
    path: '/hosting',
    description:
      'Beheerde hosting met dagelijkse back-ups, updates en monitoring.',
  },
  {
    name: 'E-mail',
    path: '/email',
    description:
      'Zakelijke e-mail op je eigen domein, ingericht en beveiligd tegen spam en phishing.',
  },
  {
    name: 'SEO',
    path: '/seo',
    description:
      'Zoekmachine optimalisatie gericht op de zoekvragen die in de regio daadwerkelijk aanvragen opleveren.',
  },
]

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#organisatie`,
  name: 'Noord Digital',
  legalName: 'Noord Digital',
  description:
    'Noord Digital zorgt dat ondernemers in Noord-Holland Noord online gevonden worden en professioneel overkomen, met websites, vormgeving, hosting, e-mail en SEO.',
  url: SITE_URL,
  telephone: '+31653689686',
  email: 'info@noorddigital.nl',
  vatID: 'NL867457521B01',
  taxID: 'NL867457521B01',
  identifier: {
    '@type': 'PropertyValue',
    name: 'KvK',
    value: '96071044',
  },
  image: `${SITE_URL}/og-image.jpg`,
  logo: 'https://cdn.magicpatterns.com/uploads/mue3TYJ7nvuB2eSYNuEqcF/Logo_favicon.svg',
  priceRange: '€€',
  currenciesAccepted: 'EUR',
  address: postalAddress,
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 52.7089,
    longitude: 4.8043,
  },
  areaServed: [
    {
      '@type': 'AdministrativeArea',
      name: 'Dijk en Waard',
    },
    {
      '@type': 'AdministrativeArea',
      name: 'Noord-Holland Noord',
    },
    {
      '@type': 'City',
      name: 'Alkmaar',
    },
    {
      '@type': 'City',
      name: 'Heerhugowaard',
    },
    {
      '@type': 'City',
      name: 'Schagen',
    },
    {
      '@type': 'City',
      name: 'Hoorn',
    },
  ],
  knowsLanguage: 'nl-NL',
  knowsAbout: [
    'Online vindbaarheid',
    'Webdesign',
    'Zoekmachine optimalisatie',
    'Huisstijl en vormgeving',
    'Webhosting',
    'Zakelijke e-mail',
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Diensten van Noord Digital',
    itemListElement: serviceCatalog.map((service) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
        url: `${SITE_URL}${service.path}`,
      },
    })),
  },
  sameAs: [
    'https://www.linkedin.com/company/noord-digital/',
    'https://www.facebook.com/profile.php?id=61572493568612',
  ],
}

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'Noord Digital',
  inLanguage: 'nl-NL',
  publisher: {
    '@id': `${SITE_URL}/#organisatie`,
  },
}

interface BreadcrumbStep {
  name: string
  path: string
}

export function buildBreadcrumbList(steps: BreadcrumbStep[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`,
      },
      ...steps.map((step, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: step.name,
        item: `${SITE_URL}${step.path}`,
      })),
    ],
  }
}

interface ServiceSchemaInput {
  name: string
  description: string
  path: string
  serviceType?: string
}

export function buildServiceSchema({
  name,
  description,
  path,
  serviceType,
}: ServiceSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType: serviceType ?? name,
    url: `${SITE_URL}${path}`,
    provider: providerReference,
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'Noord-Holland Noord',
    },
    audience: {
      '@type': 'BusinessAudience',
      name: 'Lokaal MKB en zzp in Noord-Holland Noord',
    },
  }
}

interface FAQEntry {
  question: string
  answer: string
}

export function buildFAQSchema(entries: FAQEntry[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: entry.answer,
      },
    })),
  }
}

interface ArticleSchemaInput {
  headline: string
  description: string
  path: string
  section?: string
  image?: string
}

export function buildArticleSchema({
  headline,
  description,
  path,
  section,
  image,
}: ArticleSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    inLanguage: 'nl-NL',
    articleSection: section,
    image: image ?? `${SITE_URL}/og-image.jpg`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}${path}`,
    },
    author: {
      '@id': `${SITE_URL}/#organisatie`,
    },
    publisher: {
      '@id': `${SITE_URL}/#organisatie`,
    },
  }
}

interface CaseSchemaInput {
  clientName: string
  description: string
  path: string
  sector?: string
  image?: string
  clientWebsite?: string
}

export function buildCaseStudySchema({
  clientName,
  description,
  path,
  sector,
  image,
  clientWebsite,
}: CaseSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: `Klantcase ${clientName}`,
    headline: `Klantcase ${clientName}`,
    description,
    inLanguage: 'nl-NL',
    genre: sector,
    image: image ?? `${SITE_URL}/og-image.jpg`,
    url: `${SITE_URL}${path}`,
    creator: {
      '@id': `${SITE_URL}/#organisatie`,
    },
    about: {
      '@type': 'Organization',
      name: clientName,
      ...(clientWebsite
        ? {
            url: clientWebsite,
          }
        : {}),
    },
  }
}

interface JobPostingSchemaInput {
  title: string
  description: string
  path: string
  employmentType?: string
  city?: string
  datePosted?: string
}

export function buildJobPostingSchema({
  title,
  description,
  path,
  employmentType,
  city = 'Oudkarspel',
  datePosted,
}: JobPostingSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title,
    description,
    url: `${SITE_URL}${path}`,
    inLanguage: 'nl-NL',
    employmentType,
    datePosted,
    directApply: true,
    hiringOrganization: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organisatie`,
      name: 'Noord Digital',
      sameAs: SITE_URL,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Schuit 8a',
        postalCode: '1724 BD',
        addressLocality: city,
        addressRegion: 'Noord-Holland',
        addressCountry: 'NL',
      },
    },
  }
}

interface CollectionSchemaInput {
  name: string
  description: string
  path: string
  items: {
    name: string
    path: string
  }[]
}

export function buildCollectionSchema({
  name,
  description,
  path,
  items,
}: CollectionSchemaInput) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: `${SITE_URL}${path}`,
    inLanguage: 'nl-NL',
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: `${SITE_URL}${item.path}`,
      })),
    },
  }
}
